/**
 * Toon style shared by every stylized material (ported from apps/art-test/index.html):
 * hard light bands remapped to warm light / cool hue-adjustable shadow colours, rim light,
 * height + distance fog, and inverted-hull outlines along smoothed normals (skinned too).
 *
 * Materials are cached by source material + tint so clones share programs and uniforms.
 */
import * as THREE from 'three';
import { ATMOS, CLOUD_SHADOW_GLSL } from './outdoor/atmosphere/uniforms'; // +atmos

/** One set of uniforms drives every stylized material. */
export const U = {
  uShadowColor: { value: new THREE.Color() },
  uMidColor: { value: new THREE.Color() },
  uLightColor: { value: new THREE.Color(1.07, 1.0, 0.9) },
  uKeyIntensity: { value: 1 },
  uRimColor: { value: new THREE.Color(1.0, 0.9, 0.72) },
  uRimStrength: { value: 0.42 },
  uFogColor: { value: new THREE.Color(0.1, 0.11, 0.17) },
  uFogY0: { value: -0.5 },
  uFogY1: { value: 2.2 },
  uFogAmt: { value: 0.05 },
  uFogNear: { value: 48 },
  uFogFar: { value: 120 },
  /**
   * +scenery: aerial perspective (outdoor themes; the dungeon defaults reproduce the old fog):
   * x = max distance fog (0.9 = old), y = curve (1 = smoothstep, < 1 rises sooner, then flattens),
   * z = height thinning (fog × (1 - z) on high ground: ridges and canopies stay crisper than the
   * valley floor), w = valley mist (extra low-lying haze, only in the distance).
   */
  uFogShape: { value: new THREE.Vector4(0.9, 1, 0, 0) },
  /** +scenery: tint toward the haze colour by distance applied before the fog (keeps dark shapes dark but bluer). */
  uFogTint: { value: new THREE.Color(1, 1, 1) },
  /**
   * +atmos: the far stage past uFogFar: x = where the haze reaches y.z… (yd), y = terminal fade end
   * (the far plane's cut melts away first), z = haze reached at x. Dungeons: the old 285..385 fade.
   */
  uFogEnd: { value: new THREE.Vector3(285, 385, 0.9) },
  uOutline: { value: 1.0 },
  uTime: { value: 0 },
  uFx: { value: 1.0 },
};

const BASE_LIGHT = new THREE.Color(1.07, 1.0, 0.9);
const shadowBase = new THREE.Color();
const toneLight = new THREE.Color(1, 1, 1);
const toneShadow = new THREE.Color(1, 1, 1);
function applyTone(): void {
  U.uLightColor.value.copy(BASE_LIGHT).multiply(toneLight);
  U.uShadowColor.value.copy(shadowBase).multiply(toneShadow);
  U.uMidColor.value.copy(U.uShadowColor.value).lerp(U.uLightColor.value, 0.58);
}

export function setShadowHue(h: number): void {
  // Cool shadow multiplier: light pastel keeps shadows readable instead of grey.
  shadowBase.setHSL(h / 360, 0.62, 0.8);
  applyTone();
}

/**
 * +atmos: time-of-day tint of the toon tones (the lit band and the shadow band are fixed colours,
 * so the key light's colour / intensity alone wouldn't change them). (1, 1, 1) = the plain look.
 */
export function setToneTint(light: readonly [number, number, number], shadow: readonly [number, number, number]): void {
  toneLight.setRGB(light[0], light[1], light[2]);
  toneShadow.setRGB(shadow[0], shadow[1], shadow[2]);
  applyTone();
}
setShadowHue(236);

/** 8-texel ramp for MeshToonMaterial: back half dark, thin mid band, then full light. */
export const gradientMap = (() => {
  const d = new Uint8Array([0, 0, 0, 0, 140, 255, 255, 255]);
  const t = new THREE.DataTexture(d, d.length, 1, THREE.RedFormat);
  t.minFilter = t.magFilter = THREE.NearestFilter;
  t.generateMipmaps = false;
  t.needsUpdate = true;
  return t;
})();

type Shader = THREE.WebGLProgramParametersWithUniforms;

const FOG_UNIFORM_GLSL = `
uniform vec3 uFogColor; uniform float uFogY0; uniform float uFogY1; uniform float uFogAmt; uniform float uFogNear; uniform float uFogFar;
uniform vec4 uFogShape; uniform vec3 uFogTint; uniform vec3 uFogEnd;
varying vec3 vHWorld;`;
const FOG_APPLY_GLSL = `
{
  float hDist = length(vHWorld - cameraPosition);
  float low = 1.0 - smoothstep(uFogY0, uFogY1, vHWorld.y);
  float hf = low * uFogAmt;
  float dt = smoothstep(uFogNear, uFogFar, hDist);
  // +scenery: aerial perspective. Shaped (rises sooner, saturates lower), thinner on high ground,
  // with a low valley mist in the distance; first a blue shift (dark shapes stay dark, but bluer).
  float df = pow(dt, uFogShape.y) * uFogShape.x * (1.0 - uFogShape.z * smoothstep(2.0, 26.0, vHWorld.y));
  df = max(df, low * uFogShape.w * dt);
  // +atmos: far stage (open maps, low camera): the haze keeps rising past the aerial-perspective range,
  // and the far plane's cut melts into the haze before it shows.
  df = mix(df, max(df, uFogEnd.z), smoothstep(uFogFar, uFogEnd.x, hDist));
  df = max(df, smoothstep(uFogEnd.x, uFogEnd.y, hDist));
  outgoingLight *= mix(vec3(1.0), uFogTint, sqrt(dt));
  outgoingLight = mix(outgoingLight, uFogColor, clamp(max(hf, df), 0.0, 1.0));
}`;

function injectWorldPos(shader: Shader): void {
  shader.vertexShader = shader.vertexShader.replace('#include <common>', '#include <common>\nvarying vec3 vHWorld;').replace(
    '#include <project_vertex>',
    `#include <project_vertex>
      vec4 hW = vec4(transformed, 1.0);
      #ifdef USE_INSTANCING
        hW = instanceMatrix * hW;
      #endif
      vHWorld = (modelMatrix * hW).xyz;`,
  );
  for (const k of ['uFogColor', 'uFogY0', 'uFogY1', 'uFogAmt', 'uFogNear', 'uFogFar', 'uFogShape', 'uFogTint', 'uFogEnd'] as const) shader.uniforms[k] = U[k];
}

function toonHook(shader: Shader): void {
  injectWorldPos(shader);
  for (const k of ['uShadowColor', 'uMidColor', 'uLightColor', 'uKeyIntensity', 'uRimColor', 'uRimStrength'] as const) shader.uniforms[k] = U[k];
  for (const k of ['uCloudShadow', 'uCloudTex', 'uSunDir'] as const) shader.uniforms[k] = ATMOS[k]; // +atmos
  shader.fragmentShader = shader.fragmentShader
    .replace(
      '#include <common>',
      `#include <common>
      uniform vec3 uShadowColor; uniform vec3 uMidColor; uniform vec3 uLightColor; uniform float uKeyIntensity;
      uniform vec3 uRimColor; uniform float uRimStrength;
      ${FOG_UNIFORM_GLSL}
      ${CLOUD_SHADOW_GLSL}`,
    )
    .replace(
      '#include <opaque_fragment>',
      `
      {
        // Recover the key-light term (gradient band x shadow) and remap it to custom tone colours.
        float keyL = dot(reflectedLight.directDiffuse, vec3(0.299, 0.587, 0.114));
        float baseL = max(dot(diffuseColor.rgb, vec3(0.299, 0.587, 0.114)), 1e-4);
        float lit = keyL / (baseL * uKeyIntensity);
        float b1 = smoothstep(0.24, 0.30, lit);
        float b2 = smoothstep(0.74, 0.80, lit);
        vec3 tone = mix(uShadowColor, mix(uMidColor, uLightColor, b2), b1);
        // +atmos: drifting cloud shadows soften the sunlit side toward the mid tone (outdoors only).
        tone = mix(tone, uMidColor * 0.94, (1.0 - cloudShadow(vHWorld)) * b1);
        vec3 vDir = normalize(vViewPosition);
        float fres = 1.0 - clamp(dot(normal, vDir), 0.0, 1.0);
        float rim = smoothstep(0.60, 0.66, fres) * uRimStrength;
        outgoingLight = diffuseColor.rgb * tone
          + uRimColor * rim * mix(0.5, 1.0, b1) * (0.3 + diffuseColor.rgb)
          + totalEmissiveRadiance;
      }
      ${FOG_APPLY_GLSL}
      #include <opaque_fragment>`,
    );
}

function outlineHook(shader: Shader): void {
  shader.uniforms['uOutline'] = U.uOutline;
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', '#include <common>\nattribute vec3 outlineNormal;\nuniform float uOutline;')
    .replace(
      '#include <skinning_vertex>',
      `#include <skinning_vertex>
      vec3 oN = outlineNormal;
      #ifdef USE_SKINNING
        oN = (skinMatrix * vec4(oN, 0.0)).xyz;
      #endif
      oN = normalize(oN);
      vec4 oP = vec4(transformed, 1.0);
      #ifdef USE_INSTANCING
        oP = instanceMatrix * oP;
      #endif
      vec4 oMV = modelViewMatrix * oP;
      float oScale = length(modelMatrix[0].xyz);
      transformed += oN * uOutline * clamp(-oMV.z, 5.0, 60.0) * 0.0021 / oScale;`,
    );
}

/** Smoothed normals (averaged over coincident vertices) so hulls don't crack at hard edges. */
export function addOutlineNormals(geo: THREE.BufferGeometry): void {
  if (geo.attributes['outlineNormal']) return;
  if (!geo.attributes['normal']) geo.computeVertexNormals();
  const pos = geo.attributes['position'] as THREE.BufferAttribute;
  const nor = geo.attributes['normal'] as THREE.BufferAttribute;
  const n = pos.count;
  const acc = new Map<string, [number, number, number]>();
  const keys: string[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const k = `${Math.round(pos.getX(i) * 1e4)}|${Math.round(pos.getY(i) * 1e4)}|${Math.round(pos.getZ(i) * 1e4)}`;
    keys[i] = k;
    let a = acc.get(k);
    if (!a) acc.set(k, (a = [0, 0, 0]));
    a[0] += nor.getX(i);
    a[1] += nor.getY(i);
    a[2] += nor.getZ(i);
  }
  const out = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const a = acc.get(keys[i]!)!;
    const l = Math.hypot(a[0], a[1], a[2]) || 1;
    out[i * 3] = a[0] / l;
    out[i * 3 + 1] = a[1] / l;
    out[i * 3 + 2] = a[2] / l;
  }
  geo.setAttribute('outlineNormal', new THREE.BufferAttribute(out, 3));
}

type AnyMat = THREE.Material & { color?: THREE.Color; map?: THREE.Texture | null; emissive?: THREE.Color; emissiveIntensity?: number; emissiveMap?: THREE.Texture | null };

export interface StyleOpts {
  tint?: THREE.Color;
  /** Replace emissive "Glow" materials (skeleton eyes) with this colour. */
  glowColor?: THREE.Color;
  outline?: boolean;
  castShadow?: boolean;
  receiveShadow?: boolean;
  /** Outline role: 'unit' hulls stay on at every quality; 'prop' hulls follow the quality preset. */
  outlineRole?: 'unit' | 'prop';
}

/**
 * Material cache. Lives as long as the loaded assets (shared across runs); per-run
 * materials (floor, decals) are owned and disposed by their builders.
 */
const toonCache = new Map<string, THREE.MeshToonMaterial>();
const outlineCache = new Map<string, THREE.MeshBasicMaterial>();

export function toonMaterial(orig: AnyMat, opts: Pick<StyleOpts, 'tint' | 'glowColor'> = {}): THREE.MeshToonMaterial {
  const key = orig.uuid + (opts.tint ? opts.tint.getHexString() : '') + (opts.glowColor ? `e${opts.glowColor.getHexString()}` : '');
  const hit = toonCache.get(key);
  if (hit) return hit;
  const color = orig.color ? orig.color.clone() : new THREE.Color(1, 1, 1);
  if (opts.tint) color.multiply(opts.tint);
  let emissive = orig.emissive ? orig.emissive.clone() : new THREE.Color(0, 0, 0);
  let emissiveIntensity = orig.emissiveIntensity ?? 1;
  const isGlow = /glow/i.test(orig.name || '') || emissive.r + emissive.g + emissive.b > 0.4;
  if (isGlow && opts.glowColor) {
    emissive = opts.glowColor.clone();
    emissiveIntensity = 1;
  }
  const m = new THREE.MeshToonMaterial({
    map: orig.map ?? null,
    color,
    emissive,
    emissiveIntensity,
    emissiveMap: orig.emissiveMap ?? null,
    transparent: !!orig.transparent,
    opacity: orig.opacity ?? 1,
    side: orig.side ?? THREE.FrontSide,
    alphaTest: orig.alphaTest || 0,
    gradientMap,
  });
  m.name = `toon:${orig.name}`;
  m.onBeforeCompile = toonHook;
  toonCache.set(key, m);
  return m;
}

/** Plain toon material for procedural geometry (props, braziers). Cached by colour. */
export function toonColor(hex: number, emissive = 0x000000): THREE.MeshToonMaterial {
  const key = `c${hex.toString(16)}|${emissive.toString(16)}`;
  const hit = toonCache.get(key);
  if (hit) return hit;
  const m = new THREE.MeshToonMaterial({ color: hex, emissive, gradientMap });
  m.onBeforeCompile = toonHook;
  toonCache.set(key, m);
  return m;
}

/** Make a one-off toon material (caller owns and disposes it). */
export function ownedToonMaterial(params: THREE.MeshToonMaterialParameters): THREE.MeshToonMaterial {
  const m = new THREE.MeshToonMaterial({ ...params, gradientMap });
  m.onBeforeCompile = toonHook;
  return m;
}

export function outlineMaterial(orig: AnyMat): THREE.MeshBasicMaterial {
  const at = orig.alphaTest || (orig.transparent ? 0.5 : 0);
  const key = (orig.map ? orig.map.uuid : 'nomap') + '|' + (orig.color ? orig.color.getHexString() : '') + '|' + at;
  const hit = outlineCache.get(key);
  if (hit) return hit;
  const c = (orig.color ? orig.color.clone() : new THREE.Color(1, 1, 1)).multiply(new THREE.Color(0.2, 0.15, 0.3));
  const m = new THREE.MeshBasicMaterial({ color: c, map: orig.map ?? null, side: THREE.BackSide, alphaTest: at });
  m.onBeforeCompile = outlineHook;
  outlineCache.set(key, m);
  return m;
}

/** Outline hulls registered here get toggled by settings. */
export interface HullRegistry {
  unit: Set<THREE.Object3D>;
  prop: Set<THREE.Object3D>;
}

export function addHull(mesh: THREE.Mesh, orig: AnyMat, reg: HullRegistry | null, role: 'unit' | 'prop' = 'unit'): THREE.Mesh {
  addOutlineNormals(mesh.geometry);
  const mat = outlineMaterial(orig);
  let om: THREE.Mesh;
  if ((mesh as THREE.SkinnedMesh).isSkinnedMesh) {
    const sm = mesh as THREE.SkinnedMesh;
    const hull = new THREE.SkinnedMesh(sm.geometry, mat);
    hull.position.copy(sm.position);
    hull.quaternion.copy(sm.quaternion);
    hull.scale.copy(sm.scale);
    sm.parent!.add(hull);
    hull.bind(sm.skeleton, sm.bindMatrix);
    hull.frustumCulled = false;
    om = hull;
  } else if ((mesh as THREE.InstancedMesh).isInstancedMesh) {
    const im = mesh as THREE.InstancedMesh;
    const hull = new THREE.InstancedMesh(im.geometry, mat, im.count);
    hull.instanceMatrix = im.instanceMatrix;
    hull.position.copy(im.position);
    hull.quaternion.copy(im.quaternion);
    hull.scale.copy(im.scale);
    hull.frustumCulled = im.frustumCulled;
    if (im.boundingSphere) hull.boundingSphere = im.boundingSphere.clone();
    im.parent!.add(hull);
    om = hull;
  } else {
    om = new THREE.Mesh(mesh.geometry, mat);
    mesh.add(om);
  }
  om.castShadow = false;
  om.receiveShadow = false;
  om.userData['noStyle'] = true;
  om.userData['hull'] = true;
  reg?.[role].add(om);
  return om;
}

const OUTLINE_SKIP = /eye|brow|lash|mouth|highlight|tooth|teeth|face_?|glow/i;

/** Swap every mesh under `root` to cached toon materials and (optionally) add hulls. */
export function stylize(root: THREE.Object3D, reg: HullRegistry | null, opts: StyleOpts = {}): THREE.Object3D {
  const meshes: THREE.Mesh[] = [];
  root.traverse((o) => {
    if ((o as THREE.Mesh).isMesh && !o.userData['noStyle']) meshes.push(o as THREE.Mesh);
  });
  for (const mesh of meshes) {
    const orig = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as AnyMat;
    mesh.material = toonMaterial(orig, opts);
    mesh.castShadow = opts.castShadow !== false;
    mesh.receiveShadow = opts.receiveShadow !== false;
    mesh.userData['noStyle'] = true;
    if ((mesh as THREE.SkinnedMesh).isSkinnedMesh) mesh.frustumCulled = false;
    if (opts.outline !== false && !OUTLINE_SKIP.test(orig.name || '')) addHull(mesh, orig, reg, opts.outlineRole ?? 'unit');
  }
  return root;
}

// ---------------------------------------------------------------------------
// Effect shaders
// ---------------------------------------------------------------------------

export const NOISE_GLSL = `
float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p){ vec2 i = floor(p), f = fract(p); f = f*f*(3.0-2.0*f);
  return mix(mix(hash(i), hash(i+vec2(1,0)), f.x), mix(hash(i+vec2(0,1)), hash(i+vec2(1,1)), f.x), f.y); }
float fbm(vec2 p){ float v = 0.0, a = 0.5; for (int i = 0; i < 4; i++){ v += a*noise(p); p *= 2.03; a *= 0.5; } return v; }`;

export const SIMPLE_VERT = `varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;

/** Soft additive glow disc (fake light pools under torches/braziers). Shared, never disposed per run. */
export const glowDiscMaterial = (color: [number, number, number], strength: number): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms: { uColor: { value: new THREE.Vector3(...color) }, uStrength: { value: strength }, uTime: U.uTime },
    vertexShader: `attribute float aSeed; varying vec2 vUv; varying float vSeed;
      void main(){ vUv = uv; vSeed = aSeed; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: `uniform vec3 uColor; uniform float uStrength; uniform float uTime; varying vec2 vUv; varying float vSeed;
      void main(){ float r = length(vUv * 2.0 - 1.0); if (r > 1.0) discard;
        float f = pow(1.0 - r, 2.2) * uStrength * (0.92 + 0.08 * sin(uTime * 7.0 + vSeed) * sin(uTime * 3.1 + vSeed * 2.0));
        gl_FragColor = vec4(uColor * f, f); }`,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
  });

/** Instanced camera-facing flame quads (torches/braziers). One draw call for all flames. */
export function flameMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uTime: U.uTime,
      uOuter: { value: new THREE.Vector3(1.0, 0.42, 0.08) },
      uMid: { value: new THREE.Vector3(1.0, 0.72, 0.22) },
      uCoreC: { value: new THREE.Vector3(1.0, 0.95, 0.7) },
    },
    vertexShader: `varying vec2 vUv; varying float vSeed;
      void main(){
        vUv = uv;
        vec4 c = instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
        vec3 s = vec3(length(instanceMatrix[0].xyz), length(instanceMatrix[1].xyz), 1.0);
        vSeed = fract(c.x * 0.137 + c.z * 0.311) * 20.0;
        vec4 mv = modelViewMatrix * c;
        mv.xy += position.xy * s.xy;
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: `uniform float uTime; uniform vec3 uOuter; uniform vec3 uMid; uniform vec3 uCoreC; varying vec2 vUv; varying float vSeed;
      ${NOISE_GLSL}
      void main(){
        float y = vUv.y;
        float n = noise(vec2(vUv.x * 3.0 + vSeed, y * 3.5 - uTime * 3.2));
        float w = 0.40 * (1.0 - pow(y, 1.3)) * (0.7 + 0.6 * n) * sqrt(clamp(y / 0.16, 0.0, 1.0)) + 0.005;
        float sway = (noise(vec2(vSeed, y * 1.5 - uTime * 1.8)) - 0.5) * 0.18 * y;
        float d = abs(vUv.x - 0.5 - sway) / w;
        float f = 1.0 - d - y * 0.25;
        if (f <= 0.0 || y > 0.98) discard;
        vec3 col = uOuter;
        col = mix(col, uMid, step(0.32, f));
        col = mix(col, uCoreC, step(0.62, f));
        gl_FragColor = vec4(col, 1.0);
      }`,
    side: THREE.DoubleSide,
  });
}
